import { faBolt, faPlus, faTags } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { H3 } from '@frontend/shadcn/components/typography/h3';
import { Button } from '@frontend/shadcn/components/ui/button';
import React, { useMemo, useState, useCallback } from 'react';
import { OFFER_TYPE } from '@frontend/applications/AffiliatesApp/types/globalTypes';
import {
  useApolloClient,
  useGetOffersBySearchQuery,
  useUpdateOfferMutation,
} from '@frontend/applications/AffiliatesApp/hooks';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuContent,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import { useHistory, useLocation } from 'react-router-dom';
import { SALES_APP_ID } from '@frontend/app/constants/applicationIds';
import { P } from '@frontend/shadcn/components/typography/p';
import { SectionCard } from '../../CampaignPage/components/SectionCard';
import { OfferConnectDrawer } from './OfferConnectDrawer';
import { WorkletSpecUri } from '../../constants';
import { OfferCard } from './OfferCard';
import { useProjectsApp } from '../../hooks/useProjectsApp';

interface AllOffersProps {
  projectId: number;
}

interface EmptyScreenProps {
  onConnectOffer: (offerType: OFFER_TYPE, offerConnectMode: 'connect' | 'update') => void;
  enablePromoCodeCTA: boolean;
  enableSalesLinkCTA: boolean;
}

interface ConnectOfferButtonProps {
  onClick: () => void;
  className?: string;
}

interface OfferDropdownProps {
  hasPromoCodeStage: boolean;
  hasSalesLinkStage: boolean;
  hasConnectedPromoCode: boolean;
  hasConnectedSalesLink: boolean;
  onConnectOffer: (offerType: OFFER_TYPE, offerConnectMode: 'connect' | 'update') => void;
  buttonVariant?: 'outline' | 'default';
}

const ConnectOfferButton: React.FC<ConnectOfferButtonProps> = React.memo(({ onClick, className }) => (
  <Button onClick={onClick} className={`flex items-center justify-start gap-2 ${className ?? ''}`} variant="outline">
    <FontAwesomeIcon icon={faPlus} />
    Connect Offer
  </Button>
));

ConnectOfferButton.displayName = 'ConnectOfferButton';

const OfferDropdown: React.FC<OfferDropdownProps> = React.memo(
  ({
    hasPromoCodeStage,
    hasSalesLinkStage,
    hasConnectedPromoCode,
    hasConnectedSalesLink,
    onConnectOffer,
    buttonVariant = 'default',
  }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="flex items-center gap-2 w-fit"
          disabled={hasPromoCodeStage && hasConnectedPromoCode && hasSalesLinkStage && hasConnectedSalesLink}
          variant={buttonVariant}
        >
          <FontAwesomeIcon icon={faPlus} />
          Connect Offer
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {hasPromoCodeStage && (
          <DropdownMenuItem
            className="text-sm"
            disabled={hasConnectedPromoCode}
            onClick={() => onConnectOffer(OFFER_TYPE.PROMO_CODE, 'connect')}
          >
            Connect Promo Offer
          </DropdownMenuItem>
        )}
        {hasSalesLinkStage && (
          <DropdownMenuItem
            className="text-sm"
            disabled={hasConnectedSalesLink}
            onClick={() => onConnectOffer(OFFER_TYPE.LINK, 'connect')}
          >
            Connect Sale Link Offer
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  ),
);

OfferDropdown.displayName = 'OfferDropdown';

const EmptyScreen: React.FC<EmptyScreenProps> = React.memo(
  ({ onConnectOffer, enablePromoCodeCTA, enableSalesLinkCTA }) => (
    <div className="h-[220px] w-full bg-[#F9FAFB] rounded-2xl p-16 px-20">
      <div className="flex items-center justify-between h-full">
        <div className="flex flex-col gap-2">
          <h4 className="text-grey-6 text-base font-medium m-0">Connect an Existing Offer</h4>
          <p className="text-grey-5 text-sm font-normal m-0">
            Enable your creators to share an offer to begin tracking sales.
          </p>
          <div className="flex flex-col gap-2">
            <OfferDropdown
              hasPromoCodeStage={enablePromoCodeCTA}
              hasSalesLinkStage={enableSalesLinkCTA}
              hasConnectedPromoCode={!enablePromoCodeCTA}
              hasConnectedSalesLink={!enableSalesLinkCTA}
              onConnectOffer={onConnectOffer}
            />
          </div>
        </div>
        <div className="flex items-center justify-center">
          <FontAwesomeIcon icon={faTags} className="w-[150px] h-[150px] text-[#E5E8EB]" />
        </div>
      </div>
    </div>
  ),
);

EmptyScreen.displayName = 'EmptyScreen';

const LoadingOffers: React.FC = React.memo(() => (
  <div className="flex flex-col gap-4">
    <Skeleton className="h-[50px] w-full" />
    <Skeleton className="h-[50px] w-full" />
    <Skeleton className="h-[50px] w-full" />
  </div>
));

LoadingOffers.displayName = 'LoadingOffers';

export const AllOffers: React.FC<AllOffersProps> = React.memo(({ projectId }) => {
  const [offerType, setOfferType] = useState<OFFER_TYPE | null>(null);
  const [offerConnectMode, setOfferConnectMode] = useState<'connect' | 'update'>('connect');
  const [isOfferConnectDrawerOpen, setIsOfferConnectDrawerOpen] = useState(false);
  const { worklets } = useProjectsApp({
    defaultProjectId: +projectId,
  });
  const [editOffer] = useUpdateOfferMutation();
  const staApolloClient = useApolloClient();
  const history = useHistory();
  const location = useLocation();
  const { data: connectedOffers, loading: connectedOffersLoading, refetch } = useGetOffersBySearchQuery({
    variables: {
      query: {
        programId: projectId,
      },
    },
    client: staApolloClient,
  });

  const { hasPromoCodeStage, hasSalesLinkStage } = useMemo(() => {
    const hasPromoCodeStage = worklets?.find((worklet) =>
      [
        WorkletSpecUri.PromoCodesWorkletSpecification.toString(),
        WorkletSpecUri.SalesLinkPromoCodesWorkletSpecification.toString(),
      ].includes(worklet?.specURI),
    );
    const hasSalesLinkStage = worklets?.find((worklet) =>
      [
        WorkletSpecUri.SalesLinkWorkletSpecification.toString(),
        WorkletSpecUri.SalesLinkPromoCodesWorkletSpecification.toString(),
      ].includes(worklet?.specURI),
    );
    return {
      hasPromoCodeStage: !!hasPromoCodeStage,
      hasSalesLinkStage: !!hasSalesLinkStage,
    };
  }, [worklets]);

  const { hasConnectedPromoCode, hasConnectedSalesLink } = useMemo(
    () => ({
      hasConnectedPromoCode: connectedOffers?.offers.some((offer) => offer.type === OFFER_TYPE.PROMO_CODE),
      hasConnectedSalesLink: connectedOffers?.offers.some((offer) => offer.type === OFFER_TYPE.LINK),
    }),
    [connectedOffers],
  );

  const handleOfferConnectDrawerOpen = useCallback((offerType: OFFER_TYPE, offerConnectMode: 'connect' | 'update') => {
    setOfferType(offerType);
    setIsOfferConnectDrawerOpen(true);
    setOfferConnectMode(offerConnectMode);
  }, []);

  const handleOfferConnectDrawerClose = useCallback(() => {
    setOfferType(null);
    setIsOfferConnectDrawerOpen(false);
    setOfferConnectMode('connect');
    refetch();
  }, [refetch]);

  const onDisconnectOffer = async (offerId: number) => {
    try {
      await editOffer({
        variables: {
          id: offerId,
          data: { programId: null },
        },
        client: staApolloClient,
      });
      refetch();
    } catch (error) {
      console.error(error);
    }
  };

  const rightContent = (
    <OfferDropdown
      hasPromoCodeStage={hasPromoCodeStage}
      hasSalesLinkStage={hasSalesLinkStage}
      hasConnectedPromoCode={hasConnectedPromoCode}
      hasConnectedSalesLink={hasConnectedSalesLink}
      onConnectOffer={handleOfferConnectDrawerOpen}
      buttonVariant="outline"
    />
  );

  return (
    <div className="h-[calc(100vh-64px)] bg-gray-50 flex flex-col gap-2">
      <H3 className="m-0 font-medium text-lg">Offers</H3>
      <SectionCard
        icon={<FontAwesomeIcon icon={faBolt} className="text-grey-6" />}
        subtitle="Offers"
        title="All Offers"
        description="Connect an existing offer to this campaign to start tracking sales, conversions, and payouts."
        rightContent={rightContent}
      >
        {connectedOffersLoading ? (
          <LoadingOffers />
        ) : connectedOffers?.offers?.length > 0 ? (
          <div className="flex flex-col gap-4">
            {connectedOffers?.offers?.map((offer) => (
              <OfferCard key={offer.id} offer={offer} onConnectOffer={handleOfferConnectDrawerOpen} onDisconnectOffer={onDisconnectOffer} />
            ))}
          </div>
        ) : (
          <EmptyScreen
            onConnectOffer={handleOfferConnectDrawerOpen}
            enablePromoCodeCTA={hasPromoCodeStage && !hasConnectedPromoCode}
            enableSalesLinkCTA={hasSalesLinkStage && !hasConnectedSalesLink}
          />
        )}
        <P
          className="text-grey-6 text-sm font-normal underline cursor-pointer"
          onClick={() => {
            history.push({
              ...location,
              pathname: `/app/${SALES_APP_ID}/dashboard`,
            });
          }}
        >
          Go to Sales Tracking
        </P>
      </SectionCard>
      {isOfferConnectDrawerOpen && (
        <OfferConnectDrawer
          projectId={projectId}
          offerType={offerType}
          offerConnectMode={offerConnectMode}
          isOpen={isOfferConnectDrawerOpen}
          onClose={handleOfferConnectDrawerClose}
        />
      )}
    </div>
  );
});

AllOffers.displayName = 'AllOffers';
